import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Sparkles, MessageSquare, Truck, PackageCheck, LineChart, Building2, ShieldCheck, ArrowRight, Store, UserPlus
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSettings, assetUrl } from "@/lib/settings";

const flow = [
  { n: "01", t: "Registrasi Mitra + OTP", d: "SPPG / toko / warung mendaftar dengan verifikasi WhatsApp." },
  { n: "02", t: "Order via WhatsApp Official", d: "Ketik natural, contoh: 'Ayim Karkas 350kg, Minyok Gorang 10dus.'" },
  { n: "03", t: "AI Order Processing", d: "Koreksi typo, cocokkan katalog, siapkan draft pesanan otomatis." },
  { n: "04", t: "Link Konfirmasi tokoinduk.com", d: "Mitra klik link, lihat detail lengkap, klik KONFIRMASI." },
  { n: "05", t: "Gudang + AI Route Delivery", d: "Order masuk ke gudang, rute disusun dari terdekat ke terjauh." },
  { n: "06", t: "Multi-Kurir + Duitku", d: "Cek ongkir multi-ekspedisi & pembayaran via Duitku Payment Gateway." },
];

const features = [
  { icon: MessageSquare, t: "AI Customer Service", d: "GPT memproses pesanan bahasa alami dengan koreksi typo real-time." },
  { icon: Truck, t: "AI Route Delivery", d: "Rute otomatis diurutkan dari jarak terdekat, hemat BBM & waktu tempuh." },
  { icon: PackageCheck, t: "Inventory Min/Max", d: "Deteksi stok kritis → auto WhatsApp ke supplier/petani/pabrik." },
  { icon: LineChart, t: "Finance Agentic AI", d: "Rekap transaksi, invoice, rekonsiliasi, pajak, laporan keuangan." },
  { icon: Building2, t: "Multi-Entity CV", d: "Sistem 2–50 CV dengan auto-switch omzet & tax planning architecture." },
  { icon: Store, t: "Marketplace Multi-Kota", d: "Terima seller di seluruh Indonesia, review & approve dari dashboard." },
];

export default function Landing() {
  const { settings } = useSettings();
  const [stats, setStats] = useState({ partners: 0, orders: 0, products: 0, invoices: 0, sellers_approved: 0, sales_agents: 0 });
  useEffect(() => { api.get("/stats").then(r => setStats(r.data)).catch(() => {}); }, []);
  const waNum = settings.wa_official_number || "6285147888865";
  const waDisplay = settings.wa_official_display || "0851-4788-8865";

  return (
    <div className="tech-grid">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="pill-cyan mb-6" data-testid="hero-pill">
              <Sparkles className="w-3 h-3"/> AGENTIC AI · SUPPLY CHAIN · MARKETPLACE
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              {settings.hero_title || "Pasar Induk Digital"} <br/>
              <span className="text-cyan-400 text-glow">{settings.hero_highlight || "Bertenaga AI"}</span> <br/>
              <span className="italic font-black">{settings.hero_subtitle || "untuk SPPG & UMKM Nusantara."}</span>
            </h1>
            <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed">
              {settings.hero_description || "tokoinduk.com menghubungkan mitra SPPG, seller UMKM, dan supplier via WhatsApp Official."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 flex-wrap">
              <Link to="/register">
                <Button data-testid="hero-cta-register" size="lg" className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold px-8 h-12 rounded-lg shadow-[0_0_24px_rgba(0,240,255,0.4)]">
                  {settings.cta_primary || "Daftar Pembeli"} <ArrowRight className="w-4 h-4 ml-2"/>
                </Button>
              </Link>
              <a href={`https://wa.me/${waNum}?text=${encodeURIComponent("Halo TokoInduk, saya mau pesan bahan pangan")}`} target="_blank" rel="noreferrer">
                <Button data-testid="hero-cta-wa" size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-8 h-12 rounded-lg">
                  <MessageSquare className="w-4 h-4 mr-2"/> WA {waDisplay}
                </Button>
              </a>
              <Link to="/marketplace">
                <Button data-testid="hero-cta-marketplace" size="lg" variant="outline" className="h-12 px-8 border-cyan-500/40 bg-transparent text-cyan-300 hover:bg-cyan-500/10">
                  <Store className="w-4 h-4 mr-2"/> Belanja Marketplace
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { l:"Mitra", v: stats.partners, k:"stat-partners" },
                { l:"Order", v: stats.orders, k:"stat-orders" },
                { l:"Produk", v: stats.products, k:"stat-products" },
                { l:"Invoice", v: stats.invoices, k:"stat-invoices" },
                { l:"Seller", v: stats.sellers_approved, k:"stat-sellers" },
                { l:"Sales", v: stats.sales_agents, k:"stat-sales" },
              ].map(s => (
                <div key={s.k} data-testid={s.k} className="glass rounded-xl p-3 glow-border">
                  <div className="text-[9px] uppercase tracking-widest text-cyan-400 font-mono-metric">{s.l}</div>
                  <div className="mt-1 font-mono-metric text-xl font-bold text-white">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 fade-up flex justify-center overflow-hidden">
            <div className="relative">
              <div className="absolute -inset-4 sm:-inset-6 bg-gradient-to-tr from-cyan-500/25 via-blue-600/10 to-transparent rounded-3xl blur-2xl pointer-events-none"/>
              {settings.wa_mockup_url && (
                <img
                  src={assetUrl(settings.wa_mockup_url)}
                  alt="WhatsApp mockup"
                  data-testid="hero-wa-mockup"
                  className="relative max-h-[560px] w-auto object-contain drop-shadow-[0_20px_40px_rgba(0,240,255,0.25)]"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-10">
          <div className="pill-cyan mb-3">FEATURE STACK</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Enam pilar operasional otomatis</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <Card key={i} data-testid={`feature-${i}`} className="glass glow-border rounded-2xl p-6 bg-transparent">
              <f.icon className="w-8 h-8 text-cyan-400 mb-4"/>
              <h3 className="font-display font-semibold text-white text-lg">{f.t}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.d}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* FLOW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-10">
          <div className="pill-cyan mb-3">OPERATIONAL FLOW</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Dari WhatsApp ke Dapur SPPG</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {flow.map(s => (
            <div key={s.n} className="relative rounded-2xl p-6 glass glow-border">
              <div className="font-display font-black text-6xl text-cyan-400/20 absolute right-4 top-2 leading-none">{s.n}</div>
              <div className="pill-cyan mb-3">STEP {s.n}</div>
              <div className="font-display font-bold text-white text-lg">{s.t}</div>
              <div className="text-sm text-slate-400 mt-2">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* JOIN AS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-10 text-center">
          <div className="pill-cyan mx-auto w-fit mb-3">GABUNG SEBAGAI</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Pilih peran Anda di ekosistem tokoinduk</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="glass glow-border rounded-2xl p-6">
            <UserPlus className="w-8 h-8 text-cyan-400 mb-3"/>
            <h3 className="font-display font-bold text-white text-lg">Mitra Pembeli</h3>
            <p className="text-sm text-slate-400 mt-2">SPPG, warung, resto, dapur. Order harian via WhatsApp AI.</p>
            <Link to="/register"><Button data-testid="join-mitra-btn" className="mt-4 w-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold">Daftar Mitra</Button></Link>
          </div>
          <div className="glass glow-border rounded-2xl p-6 border-emerald-500/40">
            <Store className="w-8 h-8 text-emerald-400 mb-3"/>
            <h3 className="font-display font-bold text-white text-lg">Seller / Supplier</h3>
            <p className="text-sm text-slate-400 mt-2">Jual di seluruh kota di Indonesia. Butuh review & approval admin.</p>
            <Link to="/seller/register"><Button data-testid="join-seller-btn" className="mt-4 w-full bg-emerald-400 hover:bg-emerald-300 text-slate-950 font-bold">Daftar Seller</Button></Link>
          </div>
          <div className="glass glow-border rounded-2xl p-6 border-amber-500/40">
            <Sparkles className="w-8 h-8 text-amber-400 mb-3"/>
            <h3 className="font-display font-bold text-white text-lg">Sales Freelance</h3>
            <p className="text-sm text-slate-400 mt-2">Ajak mitra baru dengan kode referal, dapat fee 0,5% dari transaksi.</p>
            <Link to="/sales/register"><Button data-testid="join-sales-btn" className="mt-4 w-full bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold">Daftar Sales</Button></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-cyan-500/10 py-6 text-center text-xs text-slate-500 font-mono-metric px-4">
        <div>Chat WhatsApp Official: <a href={`https://wa.me/${waNum}`} className="text-emerald-300 hover:text-emerald-200">{waDisplay}</a></div>
      </footer>
    </div>
  );
}
